/**
 * Voice AI — Agent Knowledgebase Tab
 * Upload and manage documents, images, and files for Agent One context.
 * Images use Haiku's vision. Documents extracted as text.
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { agentStyles } from '../../styles/agent-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { authService } from '../../services/auth-service.js';

const FILE_ICONS = {
  image: 'fa-image',
  document: 'fa-file-lines',
  audio: 'fa-waveform',
  video: 'fa-film',
};

export class AgentKnowledgeTab extends LitElement {
  static properties = {
    agent: { type: Object },
    _items: { type: Array, state: true },
    _loading: { type: Boolean, state: true },
    _uploading: { type: Boolean, state: true },
  };

  static styles = [designSystem, componentStyles, agentStyles, css`
    .kb-section {
      background: var(--color-bg);
      border: 1px solid var(--color-border);
      border-radius: 12px;
      padding: 20px 24px;
      margin-bottom: 20px;
    }
    .kb-title {
      font-size: 15px; font-weight: 600; color: var(--color-text-primary);
      margin-bottom: 4px; display: flex; align-items: center; gap: 8px;
    }
    .kb-desc { font-size: 13px; color: var(--color-text-tertiary); margin-bottom: 16px; }

    .upload-zone {
      border: 2px dashed var(--color-border); border-radius: 12px;
      padding: 32px; text-align: center; cursor: pointer;
      transition: all 0.15s ease; margin-bottom: 16px;
    }
    .upload-zone:hover { border-color: #46A546; background: #fafdf9; }
    .upload-zone.dragging { border-color: #46A546; background: #F7FAF4; }
    .upload-zone i { font-size: 28px; color: var(--color-text-tertiary); margin-bottom: 8px; display: block; }
    .upload-zone .label { font-size: 13px; color: var(--color-text-secondary); }
    .upload-zone .hint { font-size: 11px; color: var(--color-text-tertiary); margin-top: 4px; }

    .kb-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px;
    }
    .kb-item {
      border: 1px solid var(--color-border); border-radius: 10px;
      overflow: hidden; transition: border-color 0.15s ease;
    }
    .kb-item:hover { border-color: #bbd7b5; }
    .kb-thumb {
      height: 110px; background: #F5F2F0;
      display: flex; align-items: center; justify-content: center; overflow: hidden;
    }
    .kb-thumb img { width: 100%; height: 100%; object-fit: cover; }
    .kb-thumb i { font-size: 32px; color: var(--color-text-tertiary); }
    .kb-body { padding: 10px 12px; }
    .kb-name {
      font-size: 12px; font-weight: 600; color: var(--color-text-primary);
      overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
    }
    .kb-meta { display: flex; align-items: center; gap: 6px; margin-top: 4px; font-size: 11px; color: var(--color-text-tertiary); }
    .kb-type { font-size: 9px; font-weight: 600; padding: 1px 6px; border-radius: 100px; text-transform: uppercase; }
    .kb-type.image { background: #e8f4fc; color: #1e6a8a; }
    .kb-type.document { background: #fef7e0; color: #8B6914; }
    .kb-note { margin-top: 6px; }
    .kb-note input {
      width: 100%; padding: 4px 8px; border: 1px solid var(--color-border);
      border-radius: 6px; font-size: 11px; box-sizing: border-box;
    }
    .kb-note input:focus { border-color: #46A546; outline: none; }
    .kb-actions {
      display: flex; align-items: center; justify-content: space-between;
      margin-top: 8px; padding-top: 6px; border-top: 1px solid var(--color-border);
    }
    .kb-toggle-label { font-size: 10px; color: var(--color-text-tertiary); display: flex; align-items: center; gap: 4px; }
    .kb-delete { font-size: 11px; color: var(--color-error); cursor: pointer; opacity: 0.6; }
    .kb-delete:hover { opacity: 1; }
    .kb-analysis {
      margin-top: 4px; font-size: 10px; display: flex; align-items: center; gap: 4px;
    }
    .kb-analysis.done { color: #22835a; }
    .kb-analysis.pending { color: var(--color-text-tertiary); }
    .kb-analysis.analyzing { color: #9b7e2e; }
    .kb-tag-row { display: flex; align-items: center; gap: 6px; margin-top: 6px; }
    .kb-tag-select {
      font-size: 10px; padding: 2px 6px; border: 1px solid var(--color-border);
      border-radius: 6px; background: white; cursor: pointer; color: var(--color-text-primary);
    }
    .kb-tag-select:focus { border-color: #46A546; outline: none; }
    .kb-tag-label { font-size: 10px; color: var(--color-text-tertiary); }
    .kb-reanalyze {
      font-size: 9px; padding: 2px 8px; border: 1px solid var(--color-border);
      border-radius: 6px; background: white; cursor: pointer; color: var(--color-text-secondary);
      transition: all 0.15s ease;
    }
    .kb-reanalyze:hover { border-color: #46A546; color: #46A546; }
    .empty-state {
      text-align: center; padding: 32px; color: var(--color-text-tertiary); font-size: 13px;
    }
    .empty-state i { font-size: 32px; margin-bottom: 12px; display: block; opacity: 0.3; }
    @media (max-width: 768px) { .kb-grid { grid-template-columns: 1fr 1fr; } }
  `];

  constructor() {
    super();
    this._items = [];
    this._loading = false;
    this._uploading = false;
    this._lastAgentId = null;
  }

  updated(changed) {
    if (changed.has('agent') && this.agent?.id !== this._lastAgentId) {
      this._lastAgentId = this.agent?.id;
      this._loadItems();
    }
  }

  async _loadItems() {
    if (!this.agent?.id) return;
    this._loading = true;
    try {
      const resp = await authService.apiFetch(`/voice/php/api/agent-knowledge.php?agent_id=${this.agent.id}`);
      const data = await resp.json();
      if (data.success) this._items = data.items || [];
    } catch (e) { console.error('Failed to load knowledge:', e); }
    this._loading = false;
  }

  _openFilePicker() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf,.txt,.doc,.docx,.csv';
    input.multiple = true;
    input.onchange = (e) => this._handleFiles(e.target.files);
    input.click();
  }

  _onDragOver(e) { e.preventDefault(); e.currentTarget.classList.add('dragging'); }
  _onDragLeave(e) { e.currentTarget.classList.remove('dragging'); }
  _onDrop(e) { e.preventDefault(); e.currentTarget.classList.remove('dragging'); if (e.dataTransfer.files.length) this._handleFiles(e.dataTransfer.files); }

  async _handleFiles(fileList) {
    if (!this.agent?.id) return;
    this._uploading = true;
    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tags', 'knowledge');
        const token = authService.getToken();
        const uploadResp = await fetch('/voice/php/api/media.php', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        });
        const uploadData = await uploadResp.json();
        if (!uploadData.success) continue;

        await authService.apiFetch('/voice/php/api/agent-knowledge.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agent_id: this.agent.id, media_id: uploadData.media.id, context_note: '', load_on_call: 1 }),
        });
      } catch (e) { console.error('Upload failed:', e); }
    }
    this._uploading = false;
    this._loadItems();
    this.dispatchEvent(new CustomEvent('toast', { detail: { message: `${fileList.length} file(s) added`, type: 'success' }, bubbles: true, composed: true }));
  }

  async _updateNote(item, note) {
    try { await authService.apiFetch('/voice/php/api/agent-knowledge.php', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.id, context_note: note }) }); } catch (e) {}
  }

  async _toggleLoad(item) {
    const newVal = (item.load_on_call == 1) ? 0 : 1;
    try {
      await authService.apiFetch('/voice/php/api/agent-knowledge.php', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: item.id, load_on_call: newVal }) });
      this._items = this._items.map(i => i.id === item.id ? { ...i, load_on_call: newVal } : i);
    } catch (e) {}
  }

  async _updateTag(item, tag) {
    try {
      this._items = this._items.map(i => i.id === item.id ? { ...i, image_tag: tag, _analyzing: tag !== 'auto' } : i);
      const resp = await authService.apiFetch('/voice/php/api/agent-knowledge.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, image_tag: tag }),
      });
      const data = await resp.json();
      if (data.reanalysis_triggered) {
        this.dispatchEvent(new CustomEvent('toast', { detail: { message: `Re-analyzing as ${tag}...`, type: 'success' }, bubbles: true, composed: true }));
        // Reload after a delay to pick up analysis results
        setTimeout(() => this._loadItems(), 5000);
      }
      this._items = this._items.map(i => i.id === item.id ? { ...i, _analyzing: false } : i);
    } catch (e) { console.error('Tag update failed:', e); }
  }

  async _reanalyze(item) {
    this._items = this._items.map(i => i.id === item.id ? { ...i, _analyzing: true } : i);
    try {
      const resp = await authService.apiFetch('/voice/php/api/vision-analyze.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ knowledge_id: item.id, force_tag: item.image_tag || 'auto' }),
      });
      const data = await resp.json();
      if (data.success) {
        this.dispatchEvent(new CustomEvent('toast', {
          detail: { message: data.map_interpreted ? 'Map analyzed with spatial guide' : 'Image analyzed', type: 'success' },
          bubbles: true, composed: true,
        }));
        await this._loadItems();
      }
    } catch (e) { console.error('Reanalyze failed:', e); }
    this._items = this._items.map(i => i.id === item.id ? { ...i, _analyzing: false } : i);
  }

  async _deleteItem(item) {
    try {
      await authService.apiFetch(`/voice/php/api/agent-knowledge.php?id=${item.id}`, { method: 'DELETE' });
      this._items = this._items.filter(i => i.id !== item.id);
    } catch (e) {}
  }

  _formatSize(bytes) {
    if (!bytes) return '';
    const kb = Math.round(bytes / 1024);
    return kb > 1024 ? `${(kb / 1024).toFixed(1)}MB` : `${kb}KB`;
  }

  render() {
    if (!this.agent) return html``;
    return html`
      ${iconStyles}
      <div class="kb-section">
        <div class="kb-title">
          <i class="fa-sharp fa-light fa-books" style="color:#5B8A72;"></i>
          Knowledge Base
        </div>
        <div class="kb-desc">
          Upload documents, images, and files that Agent One references during calls.
          Images are analyzed visually (park maps, photos). Documents are read as text (rules, SOPs).
        </div>

        <div class="upload-zone" @click=${() => this._openFilePicker()} @dragover=${this._onDragOver} @dragleave=${this._onDragLeave} @drop=${this._onDrop}>
          ${this._uploading
            ? html`<i class="fa-sharp fa-light fa-spinner-third fa-spin"></i><div class="label">Uploading...</div>`
            : html`<i class="fa-sharp fa-light fa-cloud-arrow-up"></i><div class="label">Drop files here or click to upload</div><div class="hint">Images (JPG, PNG, WEBP) · Documents (PDF, TXT, DOCX, CSV)</div>`}
        </div>

        ${this._loading ? html`<div class="empty-state"><i class="fa-sharp fa-light fa-spinner-third fa-spin"></i> Loading...</div>` : ''}
        ${!this._loading && this._items.length === 0 ? html`<div class="empty-state"><i class="fa-sharp fa-light fa-folder-open"></i>No knowledge files yet. Upload park maps, documents, or images.</div>` : ''}
        ${this._items.length > 0 ? html`
          <div class="kb-grid">
            ${this._items.map(item => html`
              <div class="kb-item">
                <div class="kb-thumb">
                  ${item.file_type === 'image' ? html`<img src="${item.url}" alt="${item.original_name}" loading="lazy" />` : html`<i class="fa-sharp fa-light ${FILE_ICONS[item.file_type] || 'fa-file'}"></i>`}
                </div>
                <div class="kb-body">
                  <div class="kb-name" title="${item.original_name}">${item.display_name || item.original_name}</div>
                  <div class="kb-meta"><span class="kb-type ${item.file_type}">${item.file_type}</span><span>${this._formatSize(item.file_size)}</span></div>
                  ${item.file_type === 'image' ? html`
                    <div class="kb-tag-row">
                      <span class="kb-tag-label">Type:</span>
                      <select class="kb-tag-select" .value=${item.image_tag || 'auto'} @change=${e => this._updateTag(item, e.target.value)}>
                        <option value="auto">Auto-detect</option>
                        <option value="map">Map</option>
                        <option value="photo">Photo</option>
                        <option value="menu">Menu</option>
                        <option value="sign">Sign</option>
                        <option value="floorplan">Floor Plan</option>
                        <option value="document">Document</option>
                      </select>
                      <button class="kb-reanalyze" @click=${() => this._reanalyze(item)} ?disabled=${item._analyzing} title="Re-analyze with Vision API">
                        ${item._analyzing
                          ? html`<i class="fa-sharp fa-light fa-spinner-third fa-spin"></i>`
                          : html`<i class="fa-sharp fa-light fa-arrows-rotate"></i>`}
                      </button>
                    </div>
                    <div class="kb-analysis ${item.analyzed_at ? (item.image_tag === 'map' ? 'done' : 'done') : 'pending'}">
                      <i class="fa-sharp fa-light ${item.analyzed_at ? 'fa-check-circle' : 'fa-eye-slash'}"></i>
                      ${item.analyzed_at
                        ? (item.image_tag === 'map' ? 'Map analyzed (spatial guide)' : 'Vision analyzed')
                        : 'Not analyzed'}
                    </div>
                  ` : ''}
                  <div class="kb-note"><input type="text" placeholder="Context note (e.g. 'Park map')" .value=${item.context_note || ''} @change=${(e) => this._updateNote(item, e.target.value)} /></div>
                  <div class="kb-actions">
                    <label class="kb-toggle-label"><input type="checkbox" ?checked=${item.load_on_call == 1} @change=${() => this._toggleLoad(item)} /> Load on call</label>
                    <span class="kb-delete" @click=${() => this._deleteItem(item)}><i class="fa-sharp fa-light fa-trash"></i></span>
                  </div>
                </div>
              </div>
            `)}
          </div>
        ` : ''}
      </div>
    `;
  }
}

customElements.define('agent-knowledge-tab', AgentKnowledgeTab);
